import React, { useContext } from "react";
import { FaCheckCircle, FaLightbulb, FaHandsHelping, FaBullhorn } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../../context/AuthContext";
import "../TrainingGlobal.css";
import BannerImage from "./iso.jpg";

const ISODocumentation: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useContext(AuthContext);

  const handleBooking = () => {
    if (isAuthenticated) {
      navigate('/booking/iso-documentation');
    } else {
      navigate('/login', { state: { from: '/booking/iso-documentation' } });
    }
  };

  return (
    <div className="training-page">
      <div className="training-max-width">

        {/* --- HERO --- */}
        <div className="training-hero" style={{ backgroundImage: `url(${BannerImage})` }}>
          <span className="hero-tag">ISO Training</span>
          <h1 className="hero-title">ISO Documentation</h1>
          <p className="hero-subtitle">
            Ensuring structured and compliant ISO documentation
          </p>
          <button onClick={handleBooking} className="btn-hero">
            Book Now <span style={{ fontSize: '1.2em' }}>→</span>
          </button>
        </div>

        {/* --- CONTENT --- */}
        <div className="detail-content">

          <div className="content-article">
            <div className="article-section">
              <h2 className="article-heading">Purpose of ISO Documentation</h2>
              <p className="article-text">
                ISO documentation provides a structured framework for managing processes, ensuring compliance, and achieving organizational goals. It serves as a reference for maintaining consistency and quality. Proper documentation ensures that employees understand their roles and responsibilities, reducing errors and improving efficiency. It also supports audits and certifications by providing evidence of compliance with ISO standards. Comprehensive documentation fosters transparency and accountability within the organization.
              </p>
            </div>

            <div className="article-section">
              <h2 className="article-heading">SSPD Evaluation</h2>
              <p className="article-text">
                SSPD (Significant Safety and Process Deviations) evaluation identifies deviations in processes that could impact safety or compliance. Addressing these deviations ensures operational efficiency. For example, identifying a deviation in a manufacturing process can prevent defective products and reduce waste. Regular SSPD evaluations help organizations maintain high safety and quality standards while minimizing risks and costs.
              </p>
            </div>

            <div className="article-section">
              <h2 className="article-heading">Environmental Aspects and Impacts</h2>
              <p className="article-text">
                Identifying environmental aspects and their impacts helps organizations minimize their ecological footprint. This process is essential for achieving sustainability goals. For instance, understanding the impact of waste disposal or energy consumption enables organizations to implement eco-friendly practices. Regular assessments and monitoring ensure that environmental objectives are met, contributing to long-term sustainability and regulatory compliance.
              </p>
            </div>

            <div className="article-section">
              <h2 className="article-heading">HIRA Risk Ranking and Assessment Matrix</h2>
              <p className="article-text">
                The HIRA matrix evaluates risks based on likelihood and severity. It prioritizes hazards, enabling organizations to allocate resources effectively for risk mitigation. For example, a high-risk activity, such as working at height, may require immediate controls like fall protection systems. Using the HIRA matrix ensures a systematic approach to risk management and enhances workplace safety.
              </p>
            </div>

            <div className="article-section">
              <h2 className="article-heading">Hazard Identification, Risk Assessment and Control</h2>
              <p className="article-text">
                This process involves identifying hazards, assessing their risks, and implementing controls to mitigate them. It ensures a safe and compliant work environment. For example, identifying electrical hazards and implementing lockout/tagout procedures reduces the risk of accidents. Regular reviews and updates to this process ensure that emerging risks are addressed promptly, maintaining a high level of safety.
              </p>
            </div>

            <div className="article-section">
              <h2 className="article-heading">Determination of Legal Requirements</h2>
              <p className="article-text">
                Identifying and complying with legal requirements ensures that organizations meet regulatory standards and avoid penalties. It also fosters trust with stakeholders. For example, adhering to environmental regulations demonstrates a commitment to sustainability and corporate responsibility. Regular audits and updates to legal requirements ensure ongoing compliance and reduce the risk of non-conformance.
              </p>
            </div>

            <div className="article-section">
              <h2 className="article-heading">Determination of Significant Aspects and Hazards</h2>
              <p className="article-text">
                Determining significant aspects and hazards helps organizations focus on critical areas that require attention. This process enhances safety and environmental performance. For example, identifying hazardous waste as a significant aspect enables organizations to implement proper disposal methods, reducing environmental impact. Regular evaluations ensure that significant aspects and hazards are managed effectively.
              </p>
            </div>

            <div className="article-section">
              <h2 className="article-heading">Control of Significant Aspects and Hazards</h2>
              <p className="article-text">
                Controlling significant aspects and hazards involves implementing measures to reduce their impact. Regular monitoring ensures the effectiveness of these controls. For instance, installing air filtration systems to control emissions from manufacturing processes minimizes environmental impact. Continuous improvement of control measures ensures that organizations meet safety and environmental objectives.
              </p>
            </div>

            <div className="article-section">
              <h2 className="article-heading">Emergency Preparedness</h2>
              <p className="article-text">
                Emergency preparedness involves planning and training for potential emergencies. It ensures a swift and effective response to minimize risks and damages. For example, conducting fire drills and providing employees with emergency contact information enhances readiness. Regular reviews and updates to emergency plans ensure that organizations are prepared for evolving risks and scenarios.
              </p>
            </div>

            <div className="article-section">
              <h2 className="article-heading">Safe Operational Procedure</h2>
              <p className="article-text">
                Safe operational procedures provide guidelines for performing tasks safely and efficiently. They reduce the likelihood of accidents and ensure compliance with standards. For example, a safe operational procedure for handling chemicals includes steps for proper storage, labeling, and disposal. Regular training on these procedures ensures that employees understand and follow them consistently.
              </p>
            </div>

            <div className="article-section">
              <h2 className="article-heading">Life Cycle Perspective</h2>
              <p className="article-text">
                A life cycle perspective considers the environmental impacts of a product or process from creation to disposal. It promotes sustainable practices and resource conservation. For example, designing products with recyclable materials reduces waste and conserves resources. Adopting a life cycle perspective helps organizations align with sustainability goals and meet stakeholder expectations.
              </p>
            </div>

            <div className="article-section">
              <h2 className="article-heading">Document Management</h2>
              <p className="article-text">
                Document management ensures that ISO documentation is organized, accessible, and up-to-date. It facilitates audits and supports continuous improvement. For example, maintaining a centralized repository for policies and procedures ensures that employees can access the latest versions. Regular reviews and updates to documentation ensure accuracy and relevance, enhancing overall efficiency and compliance.
              </p>
            </div>
          </div>

          {/* --- SIDEBAR --- */}
          <div className="content-sidebar">
            <div className="sidebar-widget">
              <h4 className="widget-title">Key Elements</h4>
              <ul className="key-list">
                <li><FaCheckCircle className="check-icon" /> Document Control</li>
                <li><FaLightbulb className="check-icon" /> Legal Compliance</li>
                <li><FaHandsHelping className="check-icon" /> Quality Assurance</li>
                <li><FaBullhorn className="check-icon" /> Audit Readiness</li>
              </ul>
            </div>

            <div className="sidebar-widget">
              <h4 className="widget-title">Why ISO Documentation?</h4>
              <p style={{ color: '#aaa', lineHeight: '1.6', fontSize: '0.9rem' }}>
                Ensure your processes are structured, compliant, and ready for any audit.
              </p>
            </div>

            <div className="sidebar-widget" style={{ background: 'linear-gradient(135deg, #111 0%, #222 100%)', border: '1px solid #333' }}>
              <h4 className="widget-title">Get Certified</h4>
              <p style={{ color: '#ccc', marginBottom: '20px', fontSize: '0.9rem' }}>
                Start your journey towards standard compliance.
              </p>

              <button
                onClick={handleBooking}
                className="btn-primary"
                style={{ width: '100%', justifyContent: 'center' }}
              >
                Book Now
              </button>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default ISODocumentation;

